import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Input, OnDestroy, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { PlatformSyncStatus, SyncDotStatus } from 'src/shared/services/listing-optimization-settings.service';

/** What the dot renders: the aggregate status plus the per-platform breakdown for the tooltip. */
export interface SyncDotInfo {
  status: SyncDotStatus | null;
  /** Display names of the platforms that synced. */
  syncedPlatforms?: string[];
  /** Display names of the platforms whose push is still in flight. */
  pendingPlatforms?: string[];
  /** Display names of the platforms that failed. */
  failedPlatforms?: string[];
  /** Display names of the connected platforms the setting can't be pushed to. */
  unsupportedPlatforms?: string[];
  /** Display names of every platform the setting can sync to, connected or not. */
  supportedPlatforms?: string[];
}

/** One tooltip line: a platform with its own status icon, so the hover reads as an audit. */
interface SyncDotRow {
  status: PlatformSyncStatus;
  icon: string;
  text: string;
}

/**
 * A small status-light dot for a setting's channel-sync state, with a soft glow that
 * fades outward and breathes: green (synced), yellow (pending), orange (partial — some
 * platforms synced, another failed), red (failed everywhere), and a still grey (not
 * supported — no connected channel can take it). Renders nothing when the status is
 * null — a setting that reaches no channel has no dot.
 *
 * Hovering (or focusing/tapping) opens a per-platform breakdown — one line per channel
 * with its own icon: "Synced to Airbnb" / "Syncing to Booking.com" / "Failed on VRBO".
 * When the caller has no per-platform lists to give, the panel falls back to the
 * aggregate one-liner. matTooltip can't render icons, so the panel is a tiny CDK
 * overlay — the same overlay container Material tooltips use, so stacking and
 * clipping behave identically.
 */
@Component({
  selector: 'sync-status-dot',
  standalone: true,
  // None so `body.light-mode …` can theme the overlay panel — with emulated
  // encapsulation the body part of the selector would get scoped and never match.
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (status) {
      <span class="sync-status-dot" [attr.data-status]="status"
            tabindex="0" [attr.aria-label]="label"
            (mouseenter)="show()" (mouseleave)="hide()"
            (focus)="show()" (blur)="hide()"
            (keydown.escape)="hide()"></span>
      <ng-template #panel>
        <div class="sync-dot-panel" role="tooltip">
          @if (rows.length) {
            @for (row of rows; track row.text) {
              <div class="sync-dot-panel-row" [attr.data-status]="row.status">
                <span class="material-icons" aria-hidden="true">{{ row.icon }}</span>
                <span>{{ row.text }}</span>
              </div>
            }
          } @else {
            <div class="sync-dot-panel-row">{{ label }}</div>
          }
        </div>
      </ng-template>
    }
  `,
  styleUrls: ['./sync-status-dot.component.scss'],
})
export class SyncStatusDotComponent implements OnDestroy {
  @Input() sync: SyncDotInfo | null = null;

  @ViewChild('panel') private panelTemplate: TemplateRef<unknown>;

  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);
  private overlayRef: OverlayRef | null = null;

  get status(): SyncDotStatus | null {
    return this.sync?.status ?? null;
  }

  // Platform names are brands and stay as-is; the fragment before each is keyed
  // for translation. Order mirrors the sync lifecycle: done, in flight, failed.
  get rows(): SyncDotRow[] {
    const t = (text: string) => this.translate.transform(text);
    const row = (status: PlatformSyncStatus, icon: string, prefix: string) =>
      (name: string): SyncDotRow => ({ status, icon, text: `${t(prefix)} ${name}` });
    // Nothing will ever sync here, so say where it would go and why it doesn't.
    const supported = (this.sync?.supportedPlatforms ?? []).join(', ');
    if (this.status === 'not_supported' && supported) {
      return [
        row('not_supported', 'block', 'Only syncs to')(supported),
        row('not_supported', 'link_off', 'No listing connected on')(supported),
      ];
    }
    return [
      ...(this.sync?.syncedPlatforms ?? []).map(row('synced', 'check_circle', 'Synced to')),
      ...(this.sync?.pendingPlatforms ?? []).map(row('pending', 'sync', 'Syncing to')),
      ...(this.sync?.failedPlatforms ?? []).map(row('failed', 'error_outline', 'Failed on')),
      ...(this.sync?.unsupportedPlatforms ?? []).map(row('not_supported', 'block', 'Doesn’t sync to')),
    ];
  }

  // The screen-reader text: the same audit the panel shows, joined into a sentence.
  get label(): string {
    const rows = this.rows;
    if (rows.length) { return rows.map(row => row.text).join('. '); }
    const t = (text: string) => this.translate.transform(text);
    switch (this.status) {
      case 'synced': return t('Synced to all channels');
      case 'pending': return t('Syncing to channels');
      case 'partial': return t('Synced on some channels, failed on others');
      case 'failed': return t('A channel rejected this — check your connections');
      case 'not_supported': return t('Not supported on your connected channels');
      default: return '';
    }
  }

  show(): void {
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.overlay.position()
          .flexibleConnectedTo(this.elementRef)
          .withPositions([
            { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
            { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
          ]),
        scrollStrategy: this.overlay.scrollStrategies.close(),
      });
      // Tap-elsewhere on touch, where mouseleave never fires. Subscribed once per
      // overlay (not per attach) — the stream survives attach/detach cycles.
      this.overlayRef.outsidePointerEvents()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.hide());
    }
    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(new TemplatePortal(this.panelTemplate, this.viewContainerRef));
    }
  }

  hide(): void {
    this.overlayRef?.detach();
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  private readonly translate = new TranslationPipe();
}
